import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, Grid, TextField, Typography, FormControl, InputLabel, MenuItem, Select, IconButton, Autocomplete, Chip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from 'react-hot-toast';
import JoditEditor from "jodit-react";
import axios from "axios";
import useAppStore from "../appStore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Checkbox from '@mui/material/Checkbox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const schema = yup.object().shape({
    project_name: yup.string().required("Project name is required"),
    // category: yup.string().required("Category is required"),
    short_description: yup.string().required("Short description is required"),
    // description: yup.string().required("Description is required"),
    // image: yup.mixed().required("Please upload an image"),
    status: yup.string().required("Status is required"),
});

export default function AddPost() {
    const navigate = useNavigate();
    const baseURL = process.env.REACT_APP_API_URL;
    const imgURL = process.env.REACT_APP_POST_URL;
    const { token, selectedPost } = useAppStore();
    const [image, setImage] = useState("");
    const [newImage, setNewImage] = useState("");
    const [status, setStatus] = useState("");
    const [description, setDescription] = useState("");
    const contentConfig = useMemo(() => ({
        readonly: false,
        placeholder: "Enter Content",
    }), []);
    const [form, setForm] = useState({
        category: [] // This holds the selected categories
    });
    const [selectedTags, setSelectedTags] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);

    const handleCategoryChange = (event, value) => {
        setSelectedCategories(value);
        setForm({ ...form, category: value });
        selectTag(value);
    };

    const selectTag = (selectedCategories) => {
        const updatedTags = selectedCategories.map((cat) => ({
            category: cat.category,
            category_id: cat.id,
            post_id: selectedPost.id
        }));
        setSelectedTags(updatedTags);

        // console.log("Tag Selected:", updatedTags);
    };

    const { register, handleSubmit, setValue, reset, formState: { errors } } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            project_name: "",
            category: "",
            file_link: "",
            short_description: "",
            description: "",
            status: "Free",
            price: "",
        },
    });

    useEffect(() => {
        getCategory();
    }, []);

    const getCategory = async () => {
        let posturl = baseURL + "/seller-category";
        await axios
            .get(posturl, {
                headers: {
                    "Access-Control-Allow-Origin": "*",
                    "Content-type": "Application/json",
                    Authorization: `Bearer ${token}`,
                },
            })
            .then((res) => {
                setCategories(res.data);
            })
            .catch((err) => {
                console.log(err);
            });
    };


    useEffect(() => {
        console.log('selectedPost ', selectedPost);
        if (selectedPost) {
            reset(selectedPost);
            setImage(selectedPost.image);
            setDescription(selectedPost.description);
            setStatus(selectedPost.status);
            setSelectedCategories(selectedPost.categories);
        } else {
            reset({});
        }
    }, [selectedPost, reset]);


    const handlePicChange = (event) => {
        try {
            let files = event.target.files;
            let reader = new FileReader();
            reader.readAsDataURL(files[0]);
            reader.onload = (e) => {
                setImage(e.target.result);
                setNewImage(e.target.result);
            };
        } catch (e) {
            console.error(e);
        }
    };

    const onSubmit = async (data) => {
        const toastId = toast.loading('Submitting...');
        if (selectedPost) {
            try {
                const postUrl = `${baseURL}/seller-post/${selectedPost.id}`;
                await axios.put(postUrl, {
                    ...data,
                    tags: selectedTags.length > 0 ? selectedTags : '',
                    description,
                    image: newImage ? newImage : selectedPost.image,
                }, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                toast.success("Post updated successfully!");
                toast.dismiss(toastId);
                navigate("/posts");
            } catch (error) {
                toast.dismiss(toastId);
                toast.error("Failed to create post. Please try again.");
            } finally {
                toast.dismiss(toastId);
            }
        } else {
            try {
                const postUrl = `${baseURL}/seller-post`;
                await axios.post(postUrl, {
                    ...data,
                    tags: selectedTags.length > 0 ? selectedTags : '',
                    description,
                    image,
                }, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                toast.success("Post created successfully!");
                toast.dismiss(toastId);
                navigate("/posts");
            } catch (error) {
                toast.dismiss(toastId);
                toast.error("Failed to create post. Please try again.");
            } finally {
                toast.dismiss(toastId);
            }
        }
    };

    const handleContentChange = useCallback((newContent) => {
        setDescription(newContent);
        setValue("description", newContent); // Sync with react-hook-form
    }, [setValue]);

    return (
        <>
            <Toaster />
            <Box p={4}>
                <Typography variant="h5" align="center">Add Post</Typography>
                <IconButton
                    className=""
                    onClick={() => {
                        navigate("/posts");
                    }}
                >
                    <ArrowBackIcon />
                </IconButton>
                <Box height={10} />
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Project Name"
                                fullWidth
                                size="small"
                                {...register("project_name")}
                                error={!!errors.project_name}
                                helperText={errors.project_name?.message}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Autocomplete
                                multiple
                                Chip
                                id="checkboxes-tags-demo"
                                options={categories}
                                value={selectedCategories}
                                onChange={handleCategoryChange}
                                disableCloseOnSelect
                                size="small"
                                getOptionLabel={(option) => option.category}
                                renderOption={(props, option, { selected }) => {
                                    const { key, ...optionProps } = props;
                                    return (
                                        <li key={key} {...optionProps}>
                                            <Checkbox
                                                icon={icon}
                                                checkedIcon={checkedIcon}
                                                style={{ marginRight: 8 }}
                                                checked={selected}
                                            />
                                            {option.category}
                                        </li>
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} label="Category" placeholder="Category" size="small" />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Status</InputLabel>
                                <Select
                                    {...register("status")}
                                    value={status}
                                    onChange={(e) => {
                                        setStatus(e.target.value);
                                        setValue("status", e.target.value);
                                    }}
                                >
                                    <MenuItem value="Free">Free</MenuItem>
                                    <MenuItem value="Paid">Paid</MenuItem>
                                    <MenuItem value="Inactive">Inactive</MenuItem>
                                </Select>
                                {errors.status && <Typography color="error">{errors.status.message}</Typography>}
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Price"
                                fullWidth
                                size="small"
                                {...register("price")}
                                error={!!errors.price}
                                helperText={errors.price?.message}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="YouTube Link"
                                fullWidth
                                size="small"
                                {...register("youtube_link")}
                                error={!!errors.youtube_link}
                                helperText={errors.youtube_link?.message}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Preview Link"
                                fullWidth
                                size="small"
                                {...register("offer")}
                                error={!!errors.offer}
                                helperText={errors.offer?.message}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="File Link"
                                fullWidth
                                size="small"
                                {...register("file_link")}
                                error={!!errors.file_link}
                                helperText={errors.file_link?.message}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Short Description"
                                fullWidth
                                size="small"
                                {...register("short_description")}
                                error={!!errors.short_description}
                                helperText={errors.short_description?.message}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <label>Description</label>
                            <JoditEditor
                                value={description}
                                config={contentConfig}
                                onBlur={handleContentChange}
                            />
                            {errors.description && <Typography color="error">{errors.description.message}</Typography>}
                        </Grid>
                        <Grid item xs={12}>
                            <div className="flex justify-around">
                                <div>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        // onChange={(e) => handlePicChange(e, setNewImage)}
                                        onChange={handlePicChange}
                                    />
                                    {errors.image && <Typography color="error">{errors.image.message}</Typography>}
                                </div>
                                <div>
                                    {image ? (
                                        <div>
                                            {newImage ? (
                                                <img
                                                    src={newImage}
                                                    alt="Post Image"
                                                    className="max-h-[200px] max-w-[300px] rounded-lg"
                                                />
                                            ) : (
                                                <img
                                                    src={`${imgURL}/img/gallery/${selectedPost.image}`}
                                                    alt="Post Image"
                                                    className="max-h-[200px] max-w-[300px] rounded-lg"
                                                />
                                            )}
                                        </div>
                                    ) : (
                                        <img src="./photo.png" alt="upload image" className="max-h-[200px] max-w-[300px] rounded-lg" />
                                    )}
                                </div>
                            </div>
                        </Grid>
                        <Grid item xs={12}>
                            <Button variant="contained" type="submit" fullWidth>Submit</Button>
                        </Grid>
                    </Grid>
                </form>
            </Box>
        </>
    );
}
